import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useStepState } from '../../../../components/step/useStepState';
import { TranslationsKeys } from '../../../../locales/translations';

import { AcademicOfferCreateStep } from '../views/AcademicOfferCreateStep';
import { AcademicOfferSectionsStep } from '../views/AcademicOfferSectionsStep';
import { CeAcademicOfferStepEnum } from '../types';
import { AcademicOfferScheduleStep } from '../views/AcademicOfferScheduleStep';
import { AcademicOfferSummaryStep } from '../views/AcademicOfferSummaryStep';

export function useAcademicOfferCreateSteps() {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const { currentStep } = useStepState();

  const stepsConfig: {
    id: CeAcademicOfferStepEnum;
    name: string;
    component: React.FunctionComponent;
  }[] = useMemo(
    () => [
      {
        id: CeAcademicOfferStepEnum.Offer,
        name: t('wizard.steps.offer'),
        component: AcademicOfferCreateStep,
      },
      {
        id: CeAcademicOfferStepEnum.Sections,
        name: t('wizard.steps.section'),
        component: AcademicOfferSectionsStep,
      },
      {
        id: CeAcademicOfferStepEnum.Schedule,
        name: t('wizard.steps.schedule'),
        component: AcademicOfferScheduleStep,
      },
      {
        id: CeAcademicOfferStepEnum.Summary,
        name: t('wizard.steps.summary'),
        component: AcademicOfferSummaryStep,
      },
    ],
    [t],
  );

  const availableParts = useMemo(() => {
    const namesSteps: string[] = [];
    const componentsSteps: (React.FunctionComponent | undefined)[] = [];
    stepsConfig.forEach((step) => {
      namesSteps.push(step.name);
      componentsSteps.push(step.component);
    });
    return { namesSteps, componentsSteps };
  }, [stepsConfig]);

  const getIndexStep = useCallback(
    (id: string) => {
      return stepsConfig.findIndex((step) => step.id === id);
    },
    [stepsConfig],
  );

  const CurrentStepComponent = useMemo(() => {
    return availableParts.componentsSteps[currentStep];
  }, [currentStep, availableParts]);

  return {
    stepsConfig,
    ...availableParts,
    CurrentStepComponent,
    getIndexStep,
  };
}
